import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, AlertTriangle, Zap, Wrench, Sun, Cloud } from 'lucide-react';
import './GuidePage.css';

export default function EquipmentGuide() {
  return (
    <div className="guide-page">
      <Link to="/wissen" className="back-link"><ArrowLeft size={20} /> Zurück zum Wissen</Link>
      
      <article className="guide-content">
        <h1>🎒 Ausrüstung für Radtouren in Schottland</h1>
        
        <div className="warning-box">
          <AlertTriangle size={24} />
          <div>
            <strong>Schottisches Wetter ist extrem wechselhaft!</strong><br />
            "Four seasons in one day" - pack für alle Wetterlagen, auch im Sommer!
          </div>
        </div>

        <section>
          <h2>1. Kleidung - Das Zwiebelprinzip</h2>
          
          <h3>Base Layer (Unterste Schicht)</h3>
          <div className="checklist">
            <div className="check-item"><Check /> <strong>Merino-Unterwäsche</strong> - auch im Sommer! (wärmt bei Nässe)</div>
            <div className="check-item"><Check /> <strong>Funktionsshirt</strong> - langärmlig (Sonnenschutz)</div>
            <div className="check-item"><Check /> <strong>Funktionsunterhose</strong> - schnelltrocknend</div>
          </div>

          <h3>Mid Layer (Isolation)</h3>
          <div className="checklist">
            <div className="check-item"><Check /> <strong>Fleece-Jacke</strong> oder Softshell (leicht & packbar)</div>
            <div className="check-item"><Check /> <strong>Weste</strong> - für kühlere Abfahrten</div>
            <div className="check-item"><Check /> <strong>Lange Radhose</strong> oder Beinlinge</div>
          </div>

          <h3>Shell (Äußerste Schicht)</h3>
          <div className="checklist">
            <div className="check-item"><Check /> <strong>Regenjacke</strong> - wasserdicht & atmungsaktiv (Gore-Tex o.ä.)</div>
            <div className="check-item"><Check /> <strong>Regenhose</strong> - Überziehhose mit Seitenreißverschlüssen</div>
            <div className="check-item"><Check /> <strong>Windjacke</strong> - leicht, für schnelle Abfahrten</div>
          </div>

          <h3>Radbekleidung</h3>
          <div className="checklist">
            <div className="check-item"><Check /> <strong>Radhose mit Polster</strong> - mind. 2 Stück (Wechseln!)</div>
            <div className="check-item"><Check /> <strong>Radtrikots</strong> - 2-3 Stück (atmungsaktiv)</div>
            <div className="check-item"><Check /> <strong>Arm- & Beinlinge</strong> - flexibel kombinierbar</div>
          </div>

          <h3>Accessoires</h3>
          <div className="checklist">
            <div className="check-item"><Check /> <strong>Handschuhe</strong> - kurz & lang (gepolstert)</div>
            <div className="check-item"><Check /> <strong>Buff/Halstuch</strong> - gegen Wind, Sonne, Midges</div>
            <div className="check-item"><Check /> <strong>Mütze/Cap</strong> - unter dem Helm bei Kälte</div>
            <div className="check-item"><Check /> <strong>Socken</strong> - Merino, 3-4 Paar</div>
          </div>

          <div className="pro-tip">
            <h3>💡 Schottland-Spezial: Die Midges!</h3>
            <p>
              <strong>Midges</strong> (winzige Stechmücken) sind von Mai-September aktiv, besonders in 
              Highlands & am Abend. <strong>Midge-Netz</strong> für Kopf & <strong>Smidge-Spray</strong> 
              (spezielles Midge-Repellent) sind absolute Must-Haves!
            </p>
          </div>
        </section>

        <section>
          <h2>2. Helm & Sicherheit</h2>
          
          <div className="info-box">
            <h4>⚠️ Helm-Pflicht in UK?</h4>
            <p>
              Rechtlich: <strong>Keine Helmpflicht</strong> für Erwachsene in UK.<br />
              <strong>Unsere Empfehlung: IMMER Helm tragen!</strong> Single-track roads, 
              starker Wind, ungewohnter Linksverkehr - das Risiko ist real.
            </p>
          </div>

          <div className="checklist">
            <div className="check-item"><Check /> <strong>Fahrradhelm</strong> - mit guter Belüftung (CE-zertifiziert)</div>
            <div className="check-item"><Check /> <strong>Radbrille</strong> - UV-Schutz, Windschutz, Wechselgläser</div>
            <div className="check-item"><Check /> <strong>Reflektierende Weste</strong> - für Nebel & Dämmerung</div>
            <div className="check-item"><Check /> <strong>Lichter</strong> - Front (weiß) & Rück (rot) - PFLICHT bei Dunkelheit!</div>
          </div>

          <h3>Beleuchtung</h3>
          <ul>
            <li><strong>Frontlicht:</strong> Mind. 300 Lumen, USB-aufladbar</li>
            <li><strong>Rücklicht:</strong> Blink- & Dauerlicht-Funktion</li>
            <li><strong>Zusatz:</strong> Helm-Licht für Sichtbarkeit</li>
            <li><strong>Batterien/Akkus:</strong> Ersatz dabei haben</li>
          </ul>
        </section>

        <section>
          <h2>3. Fahrrad-Ausrüstung</h2>
          
          <h3>Taschen & Gepäck</h3>
          <div className="data-grid">
            <div className="data-item">
              <strong>Satteltasche:</strong> Werkzeug & Ersatzteile
            </div>
            <div className="data-item">
              <strong>Rahmentasche:</strong> Snacks, Handy, Kamera
            </div>
            <div className="data-item">
              <strong>Lenkertasche:</strong> Karte, Portemonnaie, wasserdicht
            </div>
            <div className="data-item">
              <strong>Gepäckträger-Taschen:</strong> Ortlieb o.ä. (wasserdicht!)
            </div>
          </div>

          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Wasserdichtigkeit ist KRITISCH!</strong> Schottisches Wetter bedeutet: 
            Regen ist keine Frage von "ob", sondern "wann". Packsäcke oder wasserdichte 
            Taschen sind unverzichtbar.
          </div>

          <h3>Navigation & Elektronik</h3>
          <div className="checklist">
            <div className="check-item"><Check /> <strong>GPS-Gerät oder Smartphone</strong> mit Halterung</div>
            <div className="check-item"><Check /> <strong>Offline-Karten</strong> - Mobilnetz nicht überall!</div>
            <div className="check-item"><Check /> <strong>Powerbank</strong> - mind. 10.000 mAh</div>
            <div className="check-item"><Check /> <strong>Ladekabel</strong> - USB-C, Lightning, Micro-USB</div>
            <div className="check-item"><Check /> <strong>UK-Adapter (Typ G)</strong> für Steckdosen</div>
          </div>

          <h3>Trinkflaschen & Verpflegung</h3>
          <ul>
            <li><strong>2x Trinkflaschen:</strong> Je 750ml (Highlands: Wasser oft Mangelware)</li>
            <li><strong>Trinkblase:</strong> Optional, 2-3 Liter (für lange Etappen)</li>
            <li><strong>Snacks:</strong> Energieriegel, Nüsse, Trockenfrüchte</li>
            <li><strong>Wasserfilter:</strong> Für Nachfüllen aus Bächen (optional)</li>
          </ul>
        </section>

        <section>
          <h2>4. Werkzeug & Reparatur</h2>
          
          <h3>Muss dabei sein (Unterwegs)</h3>
          <div className="checklist">
            <div className="check-item"><Check /> <strong>Multi-Tool</strong> - mit Inbus, Schraubendreher, Kettennieter</div>
            <div className="check-item"><Check /> <strong>Reifenheber</strong> - 2-3 Stück</div>
            <div className="check-item"><Check /> <strong>Ersatzschlauch</strong> - mind. 2 Stück</div>
            <div className="check-item"><Check /> <strong>Flickzeug</strong> - Patch-Kit</div>
            <div className="check-item"><Check /> <strong>Mini-Pumpe</strong> oder CO2-Kartuschen</div>
            <div className="check-item"><Check /> <strong>Kettenschloss</strong> - Ersatz</div>
          </div>

          <h3>Erweiterte Werkzeug-Ausrüstung</h3>
          <ul>
            <li><strong>Speichenschlüssel</strong> (falls du Räder zentrieren kannst)</li>
            <li><strong>Kettenöl</strong> - kleine Flasche</li>
            <li><strong>Lappen/Tuch</strong> - für Reinigung</li>
            <li><strong>Kabelbinder</strong> - 10 Stück (universal-Reparatur)</li>
            <li><strong>Isolierband</strong> - für Notfälle</li>
            <li><strong>Ersatz-Bremsbeläge</strong> (bei langen Touren)</li>
          </ul>

          <div className="info-box">
            <h4><Wrench size={20} /> Bike Shops in Schottland</h4>
            <p>
              In unserer POIs-Sektion findest du <strong>40+ Fahrrad-Werkstätten</strong> in ganz 
              Schottland. Für größere Reparaturen: Highlands haben weniger Werkstätten, 
              plane Reparaturen in größeren Städten ein.
            </p>
          </div>
        </section>

        <section>
          <h2>5. E-Bike Zusatzausrüstung</h2>
          
          <div className="info-box">
            <h4><Zap size={20} /> E-Bike in Schottland</h4>
            <p>
              E-Bikes sind perfekt für Schottland! Die Höhenmeter können brutal sein. 
              Aber: Lademöglichkeiten sind nicht überall, plane voraus!
            </p>
          </div>

          <div className="checklist">
            <div className="check-item"><Check /> <strong>Ladegerät</strong> - Original vom Hersteller</div>
            <div className="check-item"><Check /> <strong>UK-Adapter (Typ G)</strong> - mind. 2 Stück</div>
            <div className="check-item"><Check /> <strong>Ersatz-Akku</strong> - wenn möglich (teuer, aber sicher)</div>
            <div className="check-item"><Check /> <strong>Akku-Abdeckung</strong> - gegen Regen & Spritzwasser</div>
          </div>

          <h3>Reichweite & Ladeplanung</h3>
          <ul>
            <li><strong>Höhenmeter reduzieren Reichweite um 30-50%</strong></li>
            <li><strong>Eco-Modus:</strong> In flachen Regionen, spart Akku</li>
            <li><strong>Unterkunft vorab kontaktieren:</strong> Lademöglichkeit klären</li>
            <li><strong>Plan B:</strong> Bike-Shops in Highlands für Notladung nutzen</li>
            <li><strong>Zweit-Akku mieten:</strong> Manche Shops bieten das an</li>
          </ul>

          <div className="pro-tip">
            <h3>💡 E-Bike Reichweite Schottland</h3>
            <p>
              <strong>Realistische Reichweite in Highlands:</strong><br />
              - Hersteller-Angabe 100km → Rechne mit <strong>60-70km in Hügeln</strong><br />
              - Bei starkem Gegenwind oder Regen: <strong>Noch 20% weniger</strong><br />
              - Plane Etappen unter 50km oder sichere Lademöglichkeiten unterwegs
            </p>
          </div>
        </section>

        <section>
          <h2>6. Camping-Ausrüstung (Optional)</h2>
          
          <p>Für Wild Camping oder Campingplatz-Touren:</p>
          
          <div className="checklist">
            <div className="check-item"><Check /> <strong>Leichtzelt</strong> - 1-2 Personen, max. 2kg</div>
            <div className="check-item"><Check /> <strong>Schlafsack</strong> - Komfort-Temp. 0°C (auch im Sommer!)</div>
            <div className="check-item"><Check /> <strong>Isomatte</strong> - aufblasbar oder Schaum</div>
            <div className="check-item"><Check /> <strong>Kocher</strong> - Gas (leicht) oder Benzin (zuverlässig)</div>
            <div className="check-item"><Check /> <strong>Kochgeschirr</strong> - Topf, Teller, Besteck</div>
          </div>

          <div className="warning-box">
            <AlertTriangle size={20} />
            <strong>Wild Camping in Schottland:</strong> Legal unter dem "Scottish Outdoor 
            Access Code", aber Respekt für Natur & Anwohner ist Pflicht. Leave no trace!
          </div>
        </section>

        <section>
          <h2>7. Erste Hilfe & Notfall</h2>
          
          <div className="checklist">
            <div className="check-item"><Check /> <strong>Erste-Hilfe-Set</strong> - Pflaster, Verbände, Desinfektionsmittel</div>
            <div className="check-item"><Check /> <strong>Schmerzmittel</strong> - Ibuprofen o.ä.</div>
            <div className="check-item"><Check /> <strong>Sonnencreme</strong> - SPF 30+ (ja, auch in Schottland!)</div>
            <div className="check-item"><Check /> <strong>Zeckenzange</strong> - Zecken gibt es auch in Schottland</div>
            <div className="check-item"><Check /> <strong>Blasenpflaster</strong> - Compeed o.ä.</div>
          </div>

          <h3>Notfall-Kontakte</h3>
          <div className="info-box">
            <ul>
              <li><strong>Notruf UK:</strong> 999 (Polizei, Feuerwehr, Rettung)</li>
              <li><strong>NHS (Gesundheit):</strong> 111 (nicht-lebensbedrohlich)</li>
              <li><strong>Bergrettung:</strong> 999 und "Mountain Rescue" sagen</li>
              <li><strong>EHIC/Reiseversicherung:</strong> Unterlagen dabei haben</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>8. Sonstiges & Nice-to-Have</h2>
          
          <div className="data-grid">
            <div className="data-item">
              <strong>Kamera:</strong> Action-Cam oder Smartphone (wasserdicht!)
            </div>
            <div className="data-item">
              <strong>Tagebuch:</strong> Für Notizen & Erinnerungen
            </div>
            <div className="data-item">
              <strong>Fernglas:</strong> Wildlife beobachten (Hirsche, Adler)
            </div>
            <div className="data-item">
              <strong>Bücher/E-Reader:</strong> Für Regentage
            </div>
          </div>
        </section>

        <div className="pro-tip">
          <h3>🎯 Packliste nach Jahreszeit</h3>
          <h4>Sommer (Juni - August)</h4>
          <ul>
            <li>✅ Regenjacke & Überhose (immer!)</li>
            <li>✅ Lange Ärmel (Sonnenschutz & Midges)</li>
            <li>✅ Midge-Netz & Spray (absolutes Muss!)</li>
            <li>⚠️ Warme Kleidung (Abende können kühl sein)</li>
          </ul>

          <h4>Herbst/Frühling (April-Mai, Sept-Okt)</h4>
          <ul>
            <li>✅ Alles vom Sommer +</li>
            <li>✅ Warme Jacke (Fleece + Windjacke)</li>
            <li>✅ Lange Radhose</li>
            <li>✅ Mütze & warme Handschuhe</li>
            <li>⚠️ Schlafsack: Komfort-Temp. -5°C</li>
          </ul>

          <h4>Winter (Nov-März) - Nur für Hartgesottene!</h4>
          <ul>
            <li>✅ Winterkleidung komplett</li>
            <li>✅ Spike-Reifen für Eis</li>
            <li>✅ Stirnlampe (kurze Tage!)</li>
            <li>⚠️ Viele Unterkünfte geschlossen - vorher prüfen!</li>
          </ul>
        </div>

        <div className="download-section">
          <h3>📥 Checkliste herunterladen</h3>
          <button className="download-btn" onClick={() => window.print()}>
            Vollständige Packliste als PDF speichern
          </button>
        </div>
      </article>
    </div>
  );
}
